import { Refresh } from '@mui/icons-material'
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useFetchActivitiesLazyQuery } from '../../../generated'
import { FILTER_DATE_FORMAT, GRAPHQL_QUERY_POLICY, QUERY_PARAMS } from '../../constants'
import { AppContext } from '../../context'
import { ActionType } from '../../context/AppContextReducer'
import { CardBox, CardBoxTopBar } from '../../theme/styleComponents'
import { useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { Activity, isActivityEntityType } from './activityMeta'
import { ActivityPageFooter, ActivityTimeline } from './ActivityTimeline'

const PAGE_LIMIT = 20

interface ActivityLogListProps {
  page: number
  setPage: React.Dispatch<React.SetStateAction<number>>
}
export default function ActivityLogList({ page, setPage }: ActivityLogListProps) {
  const { dispatch, totalPageCount, searchValue } = useContext(AppContext)
  const [activities, setActivities] = useState<(Activity | null)[]>([])
  const [totalCount, setTotalCount] = useState(0)

  const [searchParams] = useSearchParams()

  const getFilterValues = (key: QUERY_PARAMS) => searchParams.get(key) || QUERY_PARAMS.ALL

  const entityType = getFilterValues(QUERY_PARAMS.ENTITY)
  const user = getFilterValues(QUERY_PARAMS.USER)
  const fromParam = getFilterValues(QUERY_PARAMS.FROM)
  const toParam = getFilterValues(QUERY_PARAMS.TO)

  // Fix: Ensure fromParam/toParam are valid dates before using, to avoid generating 'Invalid date'
  const fromDate = useMemo(() => {
    if (fromParam && dayjs(fromParam).isValid()) {
      return dayjs(fromParam).format(FILTER_DATE_FORMAT)
    }
    return dayjs().subtract(1, 'month').format(FILTER_DATE_FORMAT)
  }, [fromParam])

  const toDate = useMemo(() => {
    if (toParam && dayjs(toParam).isValid()) {
      return dayjs(toParam).format(FILTER_DATE_FORMAT)
    }
    return dayjs().add(1, 'day').format(FILTER_DATE_FORMAT)
  }, [toParam])

  const [fetchActivities, { loading, error, refetch }] = useFetchActivitiesLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError: error => {},
    onCompleted: data => {
      const { fetchActivities } = data || {}
      const { activities: fetchedActivities, pagination, response } = fetchActivities || {}
      if (response?.status === 200) {
        dispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: pagination?.page || 1 })
        dispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: pagination?.totalPages || 1 })
        setActivities(fetchedActivities ?? [])
        setTotalCount(pagination?.totalCount || 0)
      }
    }
  })

  useEffect(() => {
    fetchActivities({
      variables: {
        fetchActivityInput: {
          paginationOptions: {
            page,
            limit: PAGE_LIMIT
          },
          // An old link may still carry a pre-enum value ("note"); the API would reject it, so it reads as "All".
          entityType: isActivityEntityType(entityType) ? entityType : undefined,
          userId: user === QUERY_PARAMS.ALL ? undefined : user,
          fromDate,
          toDate,
          searchValue
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, entityType, user, fromDate, toDate, searchValue])

  useEffect(() => {
    return () => {
      dispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
      dispatch({ type: ActionType.SET_SEARCH_VALUE, searchValue: '' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isFiltered = [entityType, user].some(value => value !== QUERY_PARAMS.ALL) || !!searchValue

  return (
    <CardBox>
      <CardBoxTopBar>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography fontSize="18px">Feed</Typography>
          {!!totalCount && <Chip label={totalCount} size="small" />}
        </Stack>

        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={() => refetch()} disabled={loading} aria-label="Refresh activities">
              <Refresh fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </CardBoxTopBar>

      <Box p={3}>
        <ActivityTimeline
          activities={activities}
          loading={loading}
          error={!!error}
          onRetry={() => refetch()}
          emptyTitle="No activity found"
          emptyHint={isFiltered ? 'Try widening the date range or clearing filters.' : 'Try widening the date range.'}
        />
      </Box>

      <ActivityPageFooter
        page={page}
        pageSize={PAGE_LIMIT}
        totalCount={totalCount}
        totalPages={totalPageCount}
        onPageChange={setPage}
      />
    </CardBox>
  )
}
